/** Run-of-show settings (screens §F1): program defaults that pre-fill the open and release dialogs. Nothing here closes a round. */
import { useState } from "react";
import type { CohortBoard } from "@bsim/contracts";
import { Drawer } from "../theme/Drawer";
import type { Act } from "./useAct";

type RunOfShow = CohortBoard["cohort"]["settings"]["runOfShow"];

const num = (v: string): number | null => (v.trim() === "" ? null : Number(v));
const show = (v: number | null): string => (v === null ? "" : String(v));

export function SettingsDrawer({ cohortId, settings, roundNos, act, onClose }: {
  readonly cohortId: string; readonly settings: CohortBoard["cohort"]["settings"]; readonly roundNos: readonly number[]; readonly act: Act; readonly onClose: () => void;
}) {
  const ros = settings.runOfShow;
  const [minutes, setMinutes] = useState<Readonly<Record<string, string>>>(() => Object.fromEntries(roundNos.map((n) => [String(n), show(ros.countdownMinutesByRound[String(n)] ?? null)])));
  const [surprise, setSurprise] = useState(show(ros.surpriseResponseMinutes));
  const [warning, setWarning] = useState(String(ros.warningMinutes));
  const [share, setShare] = useState(String(ros.lowEngagement.atElapsedShare * 100));
  const [families, setFamilies] = useState(String(ros.lowEngagement.maxChangedFamilies));

  const [saved, setSaved] = useState(false);
  const save = async () => {
    setSaved(false);
    const runOfShow: RunOfShow = {
      countdownMinutesByRound: Object.fromEntries(roundNos.map((n) => [String(n), num(minutes[String(n)] ?? "")])),
      surpriseResponseMinutes: num(surprise), warningMinutes: Number(warning),
      lowEngagement: { atElapsedShare: Number(share) / 100, maxChangedFamilies: Number(families) },
    };
    const res = await act("cohortSettingsUpdate", { cohortId }, { runOfShow }, { headers: { "if-match": `"${settings.version}"` } });
    if (res.ok) setSaved(true);
  };

  const field = (label: string, value: string, set: (v: string) => void, hint?: string) => (
    <label className="gx-fac-field" key={label}><span>{label}</span><input className="gx-fac-input gx-num" type="number" value={value} onChange={(e) => { set(e.target.value); setSaved(false); }} />{hint === undefined ? null : <small className="gx-faint">{hint}</small>}</label>
  );

  return (
    <Drawer title="Run-of-show settings" onClose={onClose} footer="Program settings. Nothing here closes a round.">
      <div className="gx-fac-form" data-testid="drawer.settings">
        <h3 className="gx-h3">Countdown minutes per round</h3>
        {roundNos.map((n) => field(`Round ${n}`, minutes[String(n)] ?? "", (v) => setMinutes((m) => ({ ...m, [String(n)]: v })), "Blank for no countdown"))}
        {field("Surprise response minutes", surprise, setSurprise)}
        {field("Warning minutes", warning, setWarning, "Clients warn this long before the countdown ends")}
        <h3 className="gx-h3">Low engagement</h3>
        {field("Countdown elapsed (%)", share, setShare)}
        {field("Flag a team as low-engagement when it has changed at most this many decision areas", families, setFamilies)}
        <button type="button" className="gx-btn gx-btn--primary" data-testid="settings.save" onClick={() => void save()}>Save settings</button>
        {saved ? <span className="gx-small gx-muted" role="status" data-testid="settings.saved">Saved</span> : null}
      </div>
    </Drawer>
  );
}
